import { CommonModule } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Router } from "@angular/router";
import { Subscription } from "rxjs";
import { z } from "zod";
import { LucideDynamicIcon } from "@lucide/angular";
import { backend } from "@/integrations/backend/client";
import { AuthService, User } from "../core/auth.service";
import { hubLabel } from "../core/hubs";
import { ToastService } from "../core/toast.service";
import { HubFlagComponent } from "../shared/hub-flag.component";
import {
  badgeClasses,
  buttonClasses,
  cardClasses,
  inputClasses,
  labelClasses,
  progressBarStyle,
} from "../shared/ui";

interface Crew {
  id: string;
  name: string;
  description: string | null;
  owner_id: string;
  created_at: string;
}

interface CrewMember {
  id: string;
  user_id: string;
  joined_at: string;
  profiles: {
    display_name: string | null;
    avatar_url: string | null;
    hub: string | null;
  } | null;
}

interface CrewInvitation {
  id: string;
  crew_id: string;
  invited_email: string;
  status: "pending" | "accepted" | "declined";
  created_at: string;
  expires_at: string;
  crews?: { name: string };
}

interface MemberProgress {
  user_id: string;
  total_score: number;
  islands_completed: number;
}

const emailSchema = z.string().email("Please enter a valid email");
const CREW_INVITE_NOTIFICATION_KEY_PREFIX = "lina:notified-crew-invites:";

@Component({
  selector: "app-crew-page",
  standalone: true,
  imports: [CommonModule, FormsModule, HubFlagComponent, LucideDynamicIcon],
  template: `
    <div
      *ngIf="loading"
      class="min-h-screen flex items-center justify-center ocean-gradient"
    >
      <svg
        lucideIcon="loader-circle"
        class="h-8 w-8 animate-spin text-primary-foreground"
      ></svg>
    </div>

    <div *ngIf="!loading" class="min-h-screen ocean-gradient">
      <header
        class="sticky top-0 z-50 bg-background/80 backdrop-blur-md border-b border-border"
      >
        <div class="container max-w-4xl mx-auto px-4 py-4">
          <div class="flex items-center gap-4">
            <button
              [class]="buttonClasses('ghost', 'icon')"
              (click)="router.navigateByUrl('/')"
            >
              <svg lucideIcon="arrow-left" class="h-5 w-5"></svg>
            </button>
            <div class="flex items-center gap-3">
              <div class="p-2 rounded-full bg-primary/10">
                <svg lucideIcon="users" class="h-6 w-6 text-primary"></svg>
              </div>
              <h1 class="text-xl font-bold text-foreground">Your Crew</h1>
            </div>
          </div>
        </div>
      </header>

      <main class="container max-w-4xl mx-auto px-4 py-8">
        <section
          *ngIf="pendingInvitations.length > 0"
          class="mb-8 animate-in slide-in-from-top-4 fade-in"
        >
          <div [class]="cardClasses('border-secondary bg-secondary/5')">
            <header class="flex flex-col space-y-1.5 p-6">
              <h2
                class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2 text-secondary-foreground"
              >
                <svg lucideIcon="mail" class="h-5 w-5"></svg>
                Crew Invitations
              </h2>
              <p class="text-sm text-muted-foreground">
                You've been invited to join a crew!
              </p>
            </header>
            <div class="p-6 pt-0 space-y-4">
              <div
                *ngFor="let invitation of pendingInvitations"
                class="flex flex-col gap-4 rounded-lg border-2 border-secondary/30 bg-card p-4 shadow-sm sm:flex-row sm:items-center sm:justify-between"
              >
                <div class="flex items-center gap-3">
                  <div
                    class="rounded-full bg-secondary/15 p-2 text-secondary-foreground"
                  >
                    <svg lucideIcon="ship" class="h-7 w-7 text-primary"></svg>
                  </div>
                  <div>
                    <div class="flex flex-wrap items-center gap-2">
                      <p class="font-medium">{{ invitation.crews?.name }}</p>
                      <span [class]="badgeClasses('secondary', 'text-xs')">
                        New invitation
                      </span>
                    </div>
                    <p class="text-sm text-muted-foreground">
                      You have 24 hours from when it was sent to accept this
                      crew invitation.
                    </p>
                    <p class="mt-1 text-xs font-medium text-secondary-foreground">
                      {{ invitationTimeLeft(invitation) }} remaining · expires
                      {{ formatExpiration(invitation.expires_at) }}
                    </p>
                  </div>
                </div>
                <div class="flex shrink-0 gap-2">
                  <button
                    [class]="buttonClasses('default', 'sm')"
                    (click)="respondToInvitation(invitation.id, true)"
                    [disabled]="isSubmitting"
                  >
                    <svg
                      lucideIcon="circle-check-big"
                      class="h-4 w-4 mr-1"
                    ></svg>
                    Accept
                  </button>
                  <button
                    [class]="buttonClasses('outline', 'sm')"
                    (click)="respondToInvitation(invitation.id, false)"
                    [disabled]="isSubmitting"
                  >
                    <svg lucideIcon="circle-x" class="h-4 w-4 mr-1"></svg>
                    Decline
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          *ngIf="!crew"
          [class]="
            cardClasses('text-center py-12 animate-in zoom-in-95 fade-in')
          "
        >
          <div class="p-6">
            <div class="text-6xl mb-4">🚢</div>
            <h2 class="text-2xl font-bold mb-2">No Crew Yet</h2>
            <p class="text-muted-foreground mb-6 max-w-md mx-auto">
              Create a crew with 2-4 explorers. Everyone must be at the same
              island level, and members can share the same hub.
            </p>
            <button
              [class]="buttonClasses('default', 'lg', 'gap-2')"
              (click)="createDialogOpen = true"
            >
              <svg lucideIcon="anchor" class="h-5 w-5"></svg>
              Create Your Crew
            </button>
          </div>
        </section>

        <section *ngIf="crew" class="space-y-6">
          <div
            [class]="cardClasses('animate-in slide-in-from-bottom-4 fade-in')"
          >
            <header class="flex flex-col space-y-1.5 p-6">
              <div class="flex items-start justify-between">
                <div>
                  <h2
                    class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2"
                  >
                    <svg lucideIcon="ship" class="h-6 w-6 text-primary"></svg>
                    {{ crew.name }}
                  </h2>
                  <p
                    *ngIf="crew.description"
                    class="mt-1 text-sm text-muted-foreground"
                  >
                    {{ crew.description }}
                  </p>
                </div>
                <div class="flex gap-2">
                  <button
                    *ngIf="crew.owner_id === user?.id"
                    [class]="buttonClasses('outline', 'sm')"
                    [disabled]="crewSlotsRemaining <= 0"
                    (click)="inviteDialogOpen = true"
                  >
                    <svg lucideIcon="user-plus" class="h-4 w-4 mr-2"></svg>
                    Invite
                  </button>
                  <button
                    [class]="
                      buttonClasses(
                        crew.owner_id === user?.id ? 'destructive' : 'ghost',
                        'sm',
                        'gap-2'
                      )
                    "
                    [disabled]="isSubmitting"
                    (click)="leaveCrew()"
                  >
                    <svg
                      [lucideIcon]="
                        crew.owner_id === user?.id ? 'trash-2' : 'log-out'
                      "
                      class="h-4 w-4"
                    ></svg>
                    {{
                      crew.owner_id === user?.id ? "Delete Crew" : "Leave Crew"
                    }}
                  </button>
                </div>
              </div>
            </header>
          </div>

          <div
            *ngIf="members.length < 2"
            [class]="cardClasses('border-secondary/40 bg-secondary/10 p-4')"
          >
            <div class="flex gap-3">
              <svg
                lucideIcon="info"
                class="mt-0.5 h-5 w-5 shrink-0 text-secondary-foreground"
              ></svg>
              <div>
                <p class="text-sm font-semibold text-foreground">
                  Crew waiting for members
                </p>
                <p class="mt-1 text-sm leading-6 text-muted-foreground">
                  A valid crew needs at least 2 explorers. Invite another player
                  to activate this crew.
                </p>
              </div>
            </div>
          </div>

          <div
            class="grid grid-cols-3 gap-4 animate-in slide-in-from-bottom-4 fade-in"
          >
            <div [class]="cardClasses('text-center p-4')">
              <p class="text-3xl font-bold text-primary">
                {{ members.length }}/4
              </p>
              <p class="text-sm text-muted-foreground">Members</p>
            </div>
            <div [class]="cardClasses('text-center p-4')">
              <p class="text-3xl font-bold text-secondary-foreground">
                {{ crewTotalPoints }}
              </p>
              <p class="text-sm text-muted-foreground">Total Points</p>
            </div>
            <div [class]="cardClasses('text-center p-4')">
              <p class="text-3xl font-bold text-accent">
                {{ crewIslandsCompleted }}
              </p>
              <p class="text-sm text-muted-foreground">Islands Explored</p>
            </div>
          </div>

          <div
            [class]="cardClasses('animate-in slide-in-from-bottom-4 fade-in')"
          >
            <header class="flex flex-col space-y-1.5 p-6">
              <h2
                class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2"
              >
                <svg lucideIcon="users" class="h-5 w-5"></svg>
                Crew Members
              </h2>
            </header>
            <div class="p-6 pt-0 space-y-4">
              <div
                *ngFor="let member of sortedMembers; let index = index"
                class="flex flex-col gap-4 p-4 rounded-lg bg-muted/50 border border-border sm:flex-row sm:items-center"
              >
                <div class="relative shrink-0">
                  <div
                    class="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center text-xl overflow-hidden"
                  >
                    <img
                      *ngIf="member.profiles?.avatar_url"
                      [src]="member.profiles?.avatar_url"
                      alt=""
                      class="w-full h-full rounded-full object-cover"
                    />
                    <span *ngIf="!member.profiles?.avatar_url">🧑‍✈️</span>
                  </div>
                  <svg
                    *ngIf="index === 0 && members.length > 1"
                    lucideIcon="crown"
                    class="absolute -top-1 -right-1 h-4 w-4 text-secondary fill-secondary"
                  ></svg>
                </div>
                <div class="min-w-0 flex-1">
                  <div class="flex items-center gap-2">
                    <p class="font-medium truncate">
                      {{ member.profiles?.display_name || "Explorer" }}
                    </p>
                    <app-hub-flag [hub]="member.profiles?.hub" size="sm" />
                    <span class="text-xs text-muted-foreground">{{
                      hubLabel(member.profiles?.hub)
                    }}</span>
                    <span
                      *ngIf="member.user_id === crew.owner_id"
                      [class]="badgeClasses('secondary', 'text-xs')"
                      >Captain</span
                    >
                  </div>
                  <div class="flex items-center gap-4 mt-1">
                    <div
                      class="flex items-center gap-1 text-sm text-muted-foreground"
                    >
                      <svg lucideIcon="trophy" class="h-3 w-3"></svg>
                      {{ getMemberProgress(member.user_id).total_score }} pts
                    </div>
                    <div
                      class="flex items-center gap-1 text-sm text-muted-foreground"
                    >
                      <svg lucideIcon="map-pin" class="h-3 w-3"></svg>
                      {{
                        getMemberProgress(member.user_id).islands_completed
                      }}/{{ totalIslands }} islands
                    </div>
                  </div>
                </div>
                <div
                  class="flex w-full items-center justify-between gap-3 sm:w-auto sm:justify-end"
                >
                  <div class="min-w-24 flex-1 sm:w-24 sm:flex-none">
                    <div
                      class="relative h-2 w-full overflow-hidden rounded-full bg-secondary"
                    >
                      <div
                        class="h-full w-full flex-1 bg-primary transition-all"
                        [ngStyle]="
                          progressBarStyle(
                            (getMemberProgress(member.user_id)
                              .islands_completed /
                              totalIslands) *
                              100
                          )
                        "
                      ></div>
                    </div>
                  </div>
                  <button
                    *ngIf="canRemoveCrewMember(member)"
                    type="button"
                    [class]="
                      buttonClasses(
                        'outline',
                        'sm',
                        'shrink-0 border-destructive/40 text-destructive hover:bg-destructive hover:text-destructive-foreground'
                      )
                    "
                    [disabled]="isSubmitting"
                    title="Remove member from crew"
                    aria-label="Remove member from crew"
                    (click)="removeCrewMember(member)"
                  >
                    <svg
                      lucideIcon="trash-2"
                      class="h-4 w-4"
                    ></svg>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div
            *ngIf="crew.owner_id === user?.id && invitations.length > 0"
            [class]="cardClasses('animate-in slide-in-from-bottom-4 fade-in')"
          >
            <header class="flex flex-col space-y-1.5 p-6">
              <h2
                class="text-2xl font-semibold leading-none tracking-normal flex items-center gap-2"
              >
                <svg lucideIcon="clock" class="h-5 w-5"></svg>
                Invitations Waiting For Acceptance
              </h2>
              <p class="text-sm text-muted-foreground">
                These invites were sent and are still pending. Each person has
                24 hours from the sent time to accept.
              </p>
            </header>
            <div class="p-6 pt-0 space-y-2">
              <div
                *ngFor="let invitation of invitations"
                class="flex flex-col gap-3 rounded-lg border border-border bg-muted/30 p-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div class="flex items-center gap-2">
                  <svg
                    lucideIcon="mail"
                    class="h-4 w-4 text-muted-foreground"
                  ></svg>
                  <div>
                    <div class="flex flex-wrap items-center gap-2">
                      <span class="text-sm font-medium">{{
                        invitation.invited_email
                      }}</span>
                      <span [class]="badgeClasses('outline', 'text-xs')">
                        Pending acceptance
                      </span>
                    </div>
                    <p class="mt-1 text-xs text-muted-foreground">
                      Sent {{ formatExpiration(invitation.created_at) }} ·
                      expires {{ formatExpiration(invitation.expires_at) }}
                    </p>
                    <p class="text-xs font-medium text-secondary-foreground">
                      {{ invitationTimeLeft(invitation) }} remaining
                    </p>
                  </div>
                </div>
                <button
                  [class]="buttonClasses('ghost', 'sm')"
                  (click)="cancelInvitation(invitation.id)"
                >
                  <svg lucideIcon="trash-2" class="h-4 w-4"></svg>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      <div
        *ngIf="createDialogOpen"
        class="fixed inset-0 z-[90] grid place-items-center bg-black/50 p-4"
      >
        <form
          [class]="cardClasses('w-full max-w-md p-6 bg-card')"
          (ngSubmit)="createCrew()"
        >
          <h2 class="text-lg font-semibold">Create Your Crew</h2>
          <p class="mt-1 text-sm text-muted-foreground">
            Give your crew a fun tropical name!
          </p>
          <div class="space-y-4 py-4">
            <div class="space-y-2">
              <label [class]="labelClasses()" for="crew-name">Crew Name</label>
              <input
                [class]="inputClasses()"
                id="crew-name"
                name="newCrewName"
                placeholder="The Wave Riders"
                [(ngModel)]="newCrewName"
              />
            </div>
            <div class="space-y-2">
              <label [class]="labelClasses()" for="crew-description"
                >Description (optional)</label
              >
              <input
                [class]="inputClasses()"
                id="crew-description"
                name="newCrewDescription"
                placeholder="Learning AI together!"
                [(ngModel)]="newCrewDescription"
              />
            </div>
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              [class]="buttonClasses('outline', 'default', 'flex-1')"
              (click)="createDialogOpen = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              [class]="buttonClasses('default', 'default', 'flex-1')"
              [disabled]="!newCrewName.trim() || isSubmitting"
            >
              <svg
                *ngIf="isSubmitting"
                lucideIcon="loader-circle"
                class="h-4 w-4 animate-spin mr-2"
              ></svg>
              <svg
                *ngIf="!isSubmitting"
                lucideIcon="ship"
                class="h-4 w-4 mr-2"
              ></svg>
              Create Crew
            </button>
          </div>
        </form>
      </div>

      <div
        *ngIf="inviteDialogOpen"
        class="fixed inset-0 z-[90] grid place-items-center bg-black/50 p-4"
      >
        <form
          [class]="cardClasses('w-full max-w-md p-6 bg-card')"
          (ngSubmit)="inviteToCrewHandler()"
        >
          <h2 class="text-lg font-semibold">Invite a Colleague</h2>
          <p class="mt-1 text-sm text-muted-foreground">
            Invitations expire after 24 hours. The crew can have 2-4 members,
            all at the same island level. Members can share the same hub.
          </p>
          <div class="space-y-4 py-4">
            <div class="space-y-2">
              <label [class]="labelClasses()" for="invite-email"
                >Email Address</label
              >
              <input
                [class]="inputClasses()"
                id="invite-email"
                name="inviteEmail"
                type="email"
                placeholder="colleague@company.com"
                [(ngModel)]="inviteEmail"
              />
            </div>
          </div>
          <div class="flex gap-2">
            <button
              type="button"
              [class]="buttonClasses('outline', 'default', 'flex-1')"
              (click)="inviteDialogOpen = false"
            >
              Cancel
            </button>
            <button
              type="submit"
              [class]="buttonClasses('default', 'default', 'flex-1')"
              [disabled]="
                !inviteEmail.trim() || isSubmitting || crewSlotsRemaining <= 0
              "
            >
              <svg
                *ngIf="isSubmitting"
                lucideIcon="loader-circle"
                class="h-4 w-4 animate-spin mr-2"
              ></svg>
              <svg
                *ngIf="!isSubmitting"
                lucideIcon="mail"
                class="h-4 w-4 mr-2"
              ></svg>
              Send Invitation
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
})
export class CrewPageComponent implements OnInit, OnDestroy {
  private readonly auth = inject(AuthService);
  readonly router = inject(Router);
  private readonly toast = inject(ToastService);
  private readonly changeDetector = inject(ChangeDetectorRef);

  user: User | null = null;
  crew: Crew | null = null;
  members: CrewMember[] = [];
  memberProgress: MemberProgress[] = [];
  invitations: CrewInvitation[] = [];
  pendingInvitations: CrewInvitation[] = [];
  loading = true;
  createDialogOpen = false;
  inviteDialogOpen = false;
  newCrewName = "";
  newCrewDescription = "";
  inviteEmail = "";
  isSubmitting = false;
  totalIslands = 0;
  private subscription?: Subscription;

  ngOnInit() {
    this.subscription = this.auth.user$.subscribe((user) => {
      this.user = user;
      this.changeDetector.detectChanges();
      void this.fetchData();
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  get sortedMembers() {
    return [...this.members].sort(
      (a, b) =>
        this.getMemberProgress(b.user_id).total_score -
        this.getMemberProgress(a.user_id).total_score,
    );
  }

  get crewTotalPoints() {
    return this.memberProgress.reduce(
      (sum, progress) => sum + progress.total_score,
      0,
    );
  }

  get crewIslandsCompleted() {
    return this.memberProgress.reduce(
      (sum, progress) => sum + progress.islands_completed,
      0,
    );
  }

  get activePendingInvitationCount() {
    const now = Date.now();
    return this.invitations.filter(
      (invitation) => new Date(invitation.expires_at).getTime() > now,
    ).length;
  }

  get crewSlotsRemaining() {
    return Math.max(
      0,
      4 - this.members.length - this.activePendingInvitationCount,
    );
  }

  async fetchData() {
    if (!this.user) return;
    this.loading = true;
    this.changeDetector.detectChanges();

    try {
      const { count: islandCount } = await backend
        .from("islands")
        .select("*", { count: "exact", head: true });
      this.totalIslands = islandCount || 6;
      let crewId: string | null = null;

      const { data: membershipData } = await backend
        .from("crew_members")
        .select("crew_id")
        .eq("user_id", this.user.id)
        .maybeSingle();

      if (membershipData) {
        crewId = membershipData.crew_id;
      } else {
        const { data: ownedCrewData } = await backend
          .from("crews")
          .select("id")
          .eq("owner_id", this.user.id)
          .maybeSingle();
        if (ownedCrewData) {
          crewId = ownedCrewData.id;
          await backend
            .from("crew_members")
            .insert({ crew_id: ownedCrewData.id, user_id: this.user.id });
        }
      }

      if (crewId) {
        const { data: crewData } = await backend
          .from("crews")
          .select("*")
          .eq("id", crewId)
          .single();

        if (crewData) {
          this.crew = crewData;

          const { data: membersData } = await backend
            .from("crew_members")
            .select("id, user_id, joined_at")
            .eq("crew_id", crewData.id);

          if (membersData && membersData.length > 0) {
            const memberIds = membersData.map(
              (member: { user_id: string }) => member.user_id,
            );
            const { data: profilesData } = await backend
              .from("profiles")
              .select("id, display_name, avatar_url, hub")
              .in("id", memberIds);
            const profilesMap = new Map(
              (profilesData || []).map((profile: any) => [profile.id, profile]),
            );
            this.members = membersData.map((member: CrewMember) => ({
              ...member,
              profiles: profilesMap.get(member.user_id) || null,
            }));

            const { data: progressData } = await backend
              .from("island_progress")
              .select("user_id, score, completed_at")
              .in("user_id", memberIds);
            const memberIdsSet = new Set(memberIds);
            const progressMap = new Map<string, MemberProgress>();
            progressData?.forEach(
              (progress: {
                user_id: string;
                score: number;
                completed_at?: string | null;
              }) => {
                if (memberIdsSet.has(progress.user_id)) {
                  const existing = progressMap.get(progress.user_id) || {
                    user_id: progress.user_id,
                    total_score: 0,
                    islands_completed: 0,
                  };
                  existing.total_score += progress.score || 0;
                  if (progress.completed_at) existing.islands_completed += 1;
                  progressMap.set(progress.user_id, existing);
                }
              },
            );
            this.memberProgress = Array.from(progressMap.values());
          } else {
            this.members = [];
          }

          if (crewData.owner_id === this.user.id) {
            const { data: invitesData } = await backend
              .from("crew_invitations")
              .select("*")
              .eq("crew_id", crewData.id)
              .eq("status", "pending");
            this.invitations = ((invitesData as CrewInvitation[] | null) || [])
              .filter((invitation) => this.isInvitationActive(invitation));
          }
        }
      } else {
        this.crew = null;
        this.members = [];
      }

      const { data: userEmail } = await backend.auth.getUser();
      const currentEmail = (userEmail.user?.email || this.user.email || "")
        .trim()
        .toLowerCase();
      if (currentEmail) {
        const { data: pendingData } = await backend
          .from("crew_invitations")
          .select("*, crews:crew_id (name)")
          .eq("invited_email", currentEmail)
          .eq("status", "pending");

        this.pendingInvitations = (
          (pendingData as CrewInvitation[] | null) || []
        ).filter((invitation) => this.isInvitationActive(invitation));
        this.notifyAboutNewInvitations(this.pendingInvitations);
      } else {
        this.pendingInvitations = [];
      }
    } catch (error) {
      console.error("Error fetching crew data:", error);
    } finally {
      this.loading = false;
      this.changeDetector.detectChanges();
    }
  }

  async createCrew() {
    if (!this.user || !this.newCrewName.trim()) return;
    this.isSubmitting = true;
    this.changeDetector.detectChanges();

    try {
      const { data: existingMembership } = await backend
        .from("crew_members")
        .select("crew_id")
        .eq("user_id", this.user.id)
        .maybeSingle();

      if (existingMembership) {
        throw new Error(
          "You are already in a crew. Leave your current crew before creating another one.",
        );
      }

      const { data: crewData, error: crewError } = await backend
        .from("crews")
        .insert({
          name: this.newCrewName.trim(),
          description: this.newCrewDescription.trim() || null,
          owner_id: this.user.id,
        })
        .select()
        .single();

      if (crewError) throw crewError;
      const { error: memberError } = await backend
        .from("crew_members")
        .insert({ crew_id: crewData.id, user_id: this.user.id });
      if (memberError) throw memberError;

      this.toast.success("Crew created! Now invite your colleagues!");
      this.createDialogOpen = false;
      this.newCrewName = "";
      this.newCrewDescription = "";
      await this.auth.refreshUserData();
      await this.fetchData();
    } catch (error: any) {
      this.toast.error(error.message || "Failed to create crew");
    } finally {
      this.isSubmitting = false;
      this.changeDetector.detectChanges();
    }
  }

  async inviteToCrewHandler() {
    if (!this.crew || !this.inviteEmail.trim()) return;
    const invitedEmail = this.inviteEmail.trim().toLowerCase();

    try {
      emailSchema.parse(invitedEmail);
    } catch {
      this.toast.error("Please enter a valid email address");
      return;
    }

    if (this.hasActiveInvitationForEmail(invitedEmail)) {
      this.toast.info("This person already has a pending invitation.");
      return;
    }

    this.isSubmitting = true;
    this.changeDetector.detectChanges();
    try {
      const { error } = await backend.rpc("create_crew_invitation", {
        p_crew_id: this.crew.id,
        p_invited_email: invitedEmail,
      });
      if (error) throw error;

      this.toast.success(
        `Invitation sent to ${invitedEmail}. It expires in 24 hours.`,
      );
      this.inviteDialogOpen = false;
      this.inviteEmail = "";
      await this.fetchData();
    } catch (error: any) {
      const message = error.message || "Failed to send invitation";
      this.toast.error(
        message.includes("duplicate") || message.includes("pending invitation")
          ? "This person has already been invited"
          : message,
      );
    } finally {
      this.isSubmitting = false;
      this.changeDetector.detectChanges();
    }
  }

  async respondToInvitation(invitationId: string, accept: boolean) {
    this.isSubmitting = true;
    this.changeDetector.detectChanges();
    try {
      if (accept) {
        const { error } = await backend.rpc("accept_crew_invitation", {
          p_invitation_id: invitationId,
        });
        if (error) throw error;
        await this.auth.refreshUserData();
      } else {
        const { error } = await backend
          .from("crew_invitations")
          .update({ status: "declined" })
          .eq("id", invitationId);
        if (error) throw error;
      }

      this.toast.success(
        accept ? "Welcome to the crew!" : "Invitation declined",
      );
      await this.fetchData();
    } catch (error: any) {
      const message = error.message || "Failed to respond to invitation";
      this.toast.error(
        message.includes("already in another crew") ||
          message.includes("already captain")
          ? "You are already in a crew. Leave it before accepting another invitation."
          : message,
      );
    } finally {
      this.isSubmitting = false;
      this.changeDetector.detectChanges();
    }
  }

  async cancelInvitation(invitationId: string) {
    try {
      const { error } = await backend
        .from("crew_invitations")
        .delete()
        .eq("id", invitationId);
      if (error) throw error;
      this.toast.success("Invitation cancelled");
      await this.fetchData();
    } catch (error: any) {
      this.toast.error(error.message || "Failed to cancel invitation");
    }
  }

  async leaveCrew() {
    if (!this.crew || !this.user) return;

    const crewId = this.crew.id;
    const isOwner = this.crew.owner_id === this.user.id;
    const crewName = this.crew.name;

    const confirmed = isOwner
      ? confirm(`Delete "${crewName}"? This removes the crew for every member.`)
      : confirm(`Leave "${crewName}"? You can join another crew later.`);

    if (!confirmed) {
      return;
    }

    this.isSubmitting = true;
    this.changeDetector.detectChanges();
    try {
      if (isOwner) {
        const { error } = await backend.rpc("delete_crew", {
          p_crew_id: crewId,
        });
        if (error) throw error;
      } else {
        const { error } = await backend.rpc("leave_crew", {
          p_crew_id: crewId,
        });
        if (error) throw error;
      }

      this.toast.success(isOwner ? "Crew deleted" : "You have left the crew");
      await this.auth.refreshUserData();
      this.crew = null;
      this.members = [];
      this.memberProgress = [];
      this.invitations = [];
      await this.fetchData();
    } catch (error: any) {
      this.toast.error(
        error.message ||
          (isOwner ? "Failed to delete crew" : "Failed to leave crew"),
      );
    } finally {
      this.isSubmitting = false;
      this.changeDetector.detectChanges();
    }
  }

  async removeCrewMember(member: CrewMember) {
    if (!this.crew || !this.canRemoveCrewMember(member)) return;

    const memberName = this.memberDisplayName(member);
    if (
      !confirm(
        `Remove ${memberName} from "${this.crew.name}"? They can be invited again later.`,
      )
    ) {
      return;
    }

    this.isSubmitting = true;
    this.changeDetector.detectChanges();
    try {
      const { error } = await backend.rpc("remove_crew_member", {
        p_crew_id: this.crew.id,
        p_user_id: member.user_id,
      });
      if (error) throw error;

      this.toast.success(`${memberName} was removed from the crew`);
      await this.auth.refreshUserData();
      await this.fetchData();
    } catch (error: any) {
      this.toast.error(error.message || "Failed to remove crew member");
    } finally {
      this.isSubmitting = false;
      this.changeDetector.detectChanges();
    }
  }

  canRemoveCrewMember(member: CrewMember) {
    return Boolean(
      this.crew &&
        this.user &&
        this.crew.owner_id === this.user.id &&
        member.user_id !== this.user.id &&
        member.user_id !== this.crew.owner_id,
    );
  }

  memberDisplayName(member: CrewMember) {
    return member.profiles?.display_name?.trim() || "this explorer";
  }

  getMemberProgress(userId: string) {
    return (
      this.memberProgress.find((progress) => progress.user_id === userId) || {
        user_id: userId,
        total_score: 0,
        islands_completed: 0,
      }
    );
  }

  formatExpiration(value: string) {
    const expiresAt = new Date(value);
    if (Number.isNaN(expiresAt.getTime())) return "soon";

    return expiresAt.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  invitationTimeLeft(invitation: CrewInvitation) {
    const expiresAt = new Date(invitation.expires_at).getTime();
    const remainingMs = expiresAt - Date.now();
    if (!Number.isFinite(expiresAt) || remainingMs <= 0) return "expired";

    const remainingMinutes = Math.ceil(remainingMs / 60000);
    const hours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;

    if (hours >= 1) {
      return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }

    return `${minutes}m`;
  }

  private isInvitationActive(invitation: CrewInvitation) {
    const expiresAt = new Date(invitation.expires_at).getTime();
    return Number.isFinite(expiresAt) && expiresAt > Date.now();
  }

  private hasActiveInvitationForEmail(email: string) {
    return this.invitations.some(
      (invitation) =>
        invitation.invited_email.trim().toLowerCase() === email &&
        invitation.status === "pending" &&
        this.isInvitationActive(invitation),
    );
  }

  private notifyAboutNewInvitations(invitations: CrewInvitation[]) {
    if (!this.user?.id || invitations.length === 0) return;

    const storageKey = `${CREW_INVITE_NOTIFICATION_KEY_PREFIX}${this.user.id}`;
    const seenIds = this.readNotifiedInvitationIds(storageKey);
    const newInvitations = invitations.filter(
      (invitation) => !seenIds.has(invitation.id),
    );
    if (newInvitations.length === 0) return;

    this.toast.info(
      "New crew invitation! You have 24 hours from when it was sent to accept.",
    );
    newInvitations.forEach((invitation) => seenIds.add(invitation.id));
    this.writeNotifiedInvitationIds(storageKey, seenIds);
  }

  private readNotifiedInvitationIds(storageKey: string) {
    try {
      const rawValue = window.localStorage.getItem(storageKey);
      const parsedValue = rawValue ? JSON.parse(rawValue) : [];
      return new Set<string>(
        Array.isArray(parsedValue)
          ? parsedValue.filter((value): value is string => typeof value === "string")
          : [],
      );
    } catch {
      return new Set<string>();
    }
  }

  private writeNotifiedInvitationIds(storageKey: string, ids: Set<string>) {
    try {
      window.localStorage.setItem(storageKey, JSON.stringify([...ids]));
    } catch {
      // Notification persistence is best effort only.
    }
  }

  buttonClasses = buttonClasses;
  cardClasses = cardClasses;
  badgeClasses = badgeClasses;
  inputClasses = inputClasses;
  labelClasses = labelClasses;
  hubLabel = hubLabel;
  progressBarStyle = progressBarStyle;
}
